// React
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// React Query
import { useQuery } from "@tanstack/react-query";

// MUI
import { Box, Stack, SvgIcon } from "@mui/material";

// Third Party
import { useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { actions } from "@/redux/reducers/User";

// API
import { checkUserBlockStatus } from "@/Auth/api/Login/queries";

// Components
import BackdropContainer from "@/Layouts/components/BackdropContainer";
import GeneralDialog from "@/Layouts/components/Dialogs/GeneralDialog";
import IconTextButton from "@/Layouts/components/Cards/IconTextButton";

// Icons
import WalletIcon from "@/assets/icons/layers/WalletIcon";
import ClockIcon from "@/assets/icons/layers/ClockIcon";

function EmbeddedComponent({ url }) {
	return (
		<Box style={{ width: "1000px", height: "600px" }}>
			<iframe src={url} title="Embedded Pay Plan" style={{ width: "100%", height: "100%", border: "none" }} />
		</Box>
	);
}
EmbeddedComponent.propTypes = {
	url: PropTypes.string,
};

function IconToEmbed({ isSignRequest }) {
	return (
		<SvgIcon
			sx={{
				fontSize: "49.5px",
			}}
		>
			{isSignRequest ? <WalletIcon /> : <ClockIcon />}
		</SvgIcon>
	);
}
IconToEmbed.propTypes = {
	isSignRequest: PropTypes.bool,
};

function RenderDialog(props) {
	const { assignedBy, url, isSignRequest, onClickAction } = props;
	let URL = "";
	let message = `Execution of your pay plan is still in progress. Please check back at a later time.`;
	let text = "OK";

	if (isSignRequest) {
		URL = url || "";
		message = `${assignedBy || ""} requests your signature on Statement of Acceptance of ISMS Documents`;
		text = "Review & Sign";
	}

	return (
		<Stack alignItems={"center"}>
			{isSignRequest && <EmbeddedComponent url={URL} />}
			<IconTextButton
				icon={<IconToEmbed isSignRequest={isSignRequest} />}
				text={message}
				buttonText={text}
				onButtonClick={onClickAction}
			/>
		</Stack>
	);
}
RenderDialog.propTypes = {
	assignedBy: PropTypes.string,
	url: PropTypes.string,
	isSignRequest: PropTypes.bool,
	onClickAction: PropTypes.func,
};

export default function BlockedUser() {
	const navigate = useNavigate();
	const { dispatch } = useDispatch();
	const user = useSelector((state) => state.user.user);
	const isUserBlocked = Boolean(user?.isAppBlockedBy?.role);
	const isSignRequestDialog = user?.isAppBlockedBy?.role?.toUpperCase() === "ASSIGNEE";

	const [open, setOpen] = useState(false);
	const [isSignRequest, setIsSignRequest] = useState(false);

	const URL = user?.isAppBlockedBy?.url || "";

	const { refetch: refetchCheckSignature } = useQuery({
		queryKey: ["isAppBlocked"],
		queryFn: async () => {
			const isBlocked = await checkUserBlockStatus(user?.locationId, user?.username);
			redirectToLogin();
			if (isBlocked) {
				redirectToLogin();
			} else {
				dispatch(actions.SET_USER({ user: { ...user, isAppBlockedBy: null } }));
			}
			return isBlocked;
		},
		enabled: false,
	});

	const redirectToLogin = () => {
		signOut();
		window.localStorage.clear();
		navigate("/login");
	};

	const handleClick = () => {
		if (isSignRequest) {
			setIsSignRequest(false);
		} else {
			setOpen(false);
			refetchCheckSignature();
		}
	};

	useEffect(() => {
		setOpen(isUserBlocked);
		setIsSignRequest(isSignRequestDialog);

		return () => {
			setOpen(false);
		};
	}, [isUserBlocked, isSignRequestDialog]);

	return (
		<BackdropContainer open={open}>
			<GeneralDialog
				title={""}
				className={"general-dialog"}
				disableClickOut={true}
				open={open}
				sx={{ zIndex: 10002 }}
				onClose={redirectToLogin}
			>
				<RenderDialog
					assignedBy={user?.isAppBlockedBy?.assignedBy}
					isSignRequest={isSignRequest}
					url={URL}
					onClickAction={handleClick}
				/>
			</GeneralDialog>
		</BackdropContainer>
	);
}
